import React, { useState, useEffect } from "react";
import axios from "axios";
import "./WhatsAppTemplates.css";
import { showError, showSuccess, showWarning } from '../../utils/sweetAlertHelper';

// Backend API endpoints
const BASE_URL = process.env.REACT_APP_API_URL;

// Main APIs
export const TEMPLATE_API = `${BASE_URL}/templates`;
export const LEADS_API = `${BASE_URL}/leads/list`;

// WhatsApp APIs
export const MESSAGE_API = `${BASE_URL}/templates`;
export const WHATSAPP_QR_API = `${BASE_URL}/templates/whatsapp-qr`; 
export const WHATSAPP_LOGOUT_API = `${BASE_URL}/templates/logout-whatsapp`; 
export const WHATSAPP_STATUS_API = `${BASE_URL}/templates/whatsapp-status`; 


const WhatsAppTemplates = () => {
  // States
  const [templates, setTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [leadSearchTerm, setLeadSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [body, setBody] = useState("");
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [leadsTypeFilter, setLeadsTypeFilter] = useState("all");

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${TEMPLATE_API}/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const whatsappTemplates = res.data.data.filter((t) => t.type === "whatsapp");
        setTemplates(whatsappTemplates);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch leads
  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(LEADS_API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const leadsData = res.data.data || res.data || [];
      setLeads(leadsData);
      setFilteredLeads(leadsData);
    } catch (err) {
      console.error(err);
    }
  };

  // Check WhatsApp status
  const checkWhatsAppStatus = async () => {
    try {
      const res = await axios.get(WHATSAPP_STATUS_API);
      setIsLoggedIn(res.data.loggedIn || false);
    } catch (err) {
      console.error("Error checking WhatsApp status:", err);
      setIsLoggedIn(false);
    }
  };

  // Poll QR code and login status
  useEffect(() => {
    const fetchQR = async () => {
      try {
        const res = await axios.get(WHATSAPP_QR_API);
        
        // Check different possible response formats
        if (res.data.qr) {
          setQrCode(res.data.qr);
          setIsLoggedIn(false);
        } else if (res.data.loggedIn !== undefined) {
          setIsLoggedIn(res.data.loggedIn);
          setQrCode(null);
        } else {
          // If no specific data, check status separately
          await checkWhatsAppStatus();
        }
      } catch (err) {
        console.error("Error fetching QR:", err);
        setQrCode(null);
        await checkWhatsAppStatus();
      }
    };

    fetchQR();

    const interval = setInterval(fetchQR, 3000); // Check every 3 seconds
    return () => clearInterval(interval);
  }, []);

  // Logout WhatsApp
  const handleWhatsAppLogout = async () => {
    if (!window.confirm("Are you sure you want to logout from WhatsApp?")) return;
    try {
      await axios.post(WHATSAPP_LOGOUT_API);
      setIsLoggedIn(false);
      setQrCode(null);
      showSuccess("Logged out from WhatsApp ✅");
    } catch (err) {
      console.error(err);
      showError("Failed to logout ❌");
    }
  };

  // Initialize
  useEffect(() => {
    fetchTemplates();
    fetchLeads();
    checkWhatsAppStatus(); // Initial status check
  }, []);

  // Filter leads based on search term and filters
  useEffect(() => {
    let filtered = leads;
    
    // Apply search term filter
    if (leadSearchTerm.trim()) {
      filtered = filtered.filter(
        (l) =>
          (l.name || "").toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
          (l.phone || "").includes(leadSearchTerm) ||
          (l.company || "").toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
          (l.email || "").toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
          (l.country || "").toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
          (l.website || "").toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
          (l.linkedin || "").toLowerCase().includes(leadSearchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(l => l.status === statusFilter);
    }
    
    // Apply type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(l => l.type === typeFilter);
    }
    
    // Apply source filter
    if (sourceFilter !== "all") {
      filtered = filtered.filter(l => l.source === sourceFilter);
    }
    
    // Apply leads type filter
    if (leadsTypeFilter !== "all") {
      filtered = filtered.filter(l => l.leadsType === leadsTypeFilter);
    }
    
    setFilteredLeads(filtered);
  }, [leadSearchTerm, leads, statusFilter, typeFilter, sourceFilter, leadsTypeFilter]);

  // Add/Edit Template
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      if (editingTemplate && editingTemplate._id) {
        await axios.put(
          `${TEMPLATE_API}/update/${editingTemplate._id}`,
          { templateName, body },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSuccess("Template updated successfully ✅");
      } else {
        await axios.post(
          `${TEMPLATE_API}/add`,
          { type: "whatsapp", templateName, body },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSuccess("Template added successfully ✅");
      }
      setTemplateName("");
      setBody("");
      setEditingTemplate(null);
      setIsFormVisible(false);
      fetchTemplates();
    } catch (err) {
      console.error(err);
      showError("Failed to save template ❌");
    }
  };

  // Edit template
  const handleEdit = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.templateName);
    setBody(template.body);
    setIsFormVisible(true);
  };

  // Delete template
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${TEMPLATE_API}/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSuccess("Template deleted successfully ✅");
      fetchTemplates();
    } catch (err) {
      console.error(err);
      showError("Failed to delete template ❌");
    }
  };

  // Bulk send WhatsApp
  const handleBulkSend = async (templateId) => {
    if (!selectedLeads.length) {
      showWarning("Select at least one lead ⚠️");
      return;
    }
    if (!isLoggedIn) {
      showWarning("WhatsApp is not connected. Please login first ⚠️");
      return;
    }
    try {
      await axios.post(
        `${MESSAGE_API}/send-bulk`,
        { messageId: templateId, leadIds: selectedLeads },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      showSuccess("Messages sent successfully ✅");
    } catch (err) {
      console.error(err);
      showError("Failed to send messages ❌");
    }
  };

  // Lead selection
  const toggleLead = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const selectAllLeads = () => setSelectedLeads(filteredLeads.map((l) => l._id));
  const deselectAllLeads = () => setSelectedLeads([]);

  const filteredTemplates = templates.filter(
    (t) =>
      (t.templateName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.body || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get unique values for filters
  const statusOptions = ["all", "new", "contacted", "Converted", "disqualified", "inprocess", "demo-scheduled", "proposal-shared"];
  const typeOptions = ["all", "initial_1", "initial_2", "followup_1", "followup_2", "closure_1", "closure_2", "closed"];
  const sourceOptions = ["all", "Whatsapp", "Linkedin", "Instagram", "Telegram", "Igblive", "Meet-Alfred", "chatgpt"];
  const leadsTypeOptions = ["all", "it", "b2b"];

  if (isLoading) return <div className="loading">Loading templates...</div>;

  return (
    <div className="whatsapp-templates">
      <header className="templates-header">
        <h2>📲 WhatsApp Templates Manager</h2>
        <p>Create, manage and bulk send WhatsApp templates</p>
      </header>

      {/* WhatsApp Login / Logout */}
      <div className="whatsapp-login-section">
        {isLoggedIn ? (
          <div className="whatsapp-connected">
            <p>✅ WhatsApp Connected</p>
            <button className="logout-btn" onClick={handleWhatsAppLogout}>
              🔒 Logout WhatsApp
            </button>
          </div>
        ) : (
          <div className="qr-login">
            <h3>📱 Scan QR to login to WhatsApp</h3>
            {qrCode ? (
              <img src={qrCode} alt="WhatsApp QR" style={{ width: 250, height: 250 }} />
            ) : (
              <p>Waiting for QR code...</p>
            )}
          </div>
        )}
      </div>

      {/* Add Template */}
      {!isFormVisible && (
        <button className="add-template-btn" onClick={() => setIsFormVisible(true)}>
          ➕ Add Template
        </button>
      )}

      {/* Template Form */}
      {isFormVisible && (
        <div className="template-form-container">
          <form onSubmit={handleFormSubmit} className="template-form">
            <input
              type="text"
              placeholder="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
            />
            <textarea
              placeholder="Template Body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
            />
            <div className="form-buttons">
              <button type="submit">{editingTemplate ? "Update" : "Add"}</button>
              <button
                type="button"
                onClick={() => {
                  setIsFormVisible(false);
                  setEditingTemplate(null);
                  setTemplateName("");
                  setBody("");
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Leads Section */}
      <div className="leads-section">
        <div className="leads-header">
          <h3>👥 Leads ({leads.length})</h3>
          <input
            type="text"
            placeholder="Search leads..."
            value={leadSearchTerm}
            onChange={(e) => setLeadSearchTerm(e.target.value)}
          />
          
          {/* Filter Controls */}
          <div className="leads-filters">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {statusOptions.map(option => (
                <option key={option} value={option}>
                  {option === "all" ? "All Statuses" : option}
                </option>
              ))}
            </select>
            
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              {typeOptions.map(option => (
                <option key={option} value={option}>
                  {option === "all" ? "All Types" : option}
                </option>
              ))}
            </select>
            
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              {sourceOptions.map(option => (
                <option key={option} value={option}>
                  {option === "all" ? "All Sources" : option}
                </option>
              ))}
            </select>
            
            <select value={leadsTypeFilter} onChange={(e) => setLeadsTypeFilter(e.target.value)}>
              {leadsTypeOptions.map(option => (
                <option key={option} value={option}>
                  {option === "all" ? "All Lead Types" : option}
                </option>
              ))}
            </select>
          </div>
          
          <div className="leads-actions">
            <button onClick={selectAllLeads}>Select All</button>
            <button onClick={deselectAllLeads}>Deselect All</button>
          </div>
          <div>Selected: {selectedLeads.length}</div>
        </div>
        <ul className="leads-list">
          {filteredLeads.map((lead) => (
            <li key={lead._id}>
              <label>
                <input
                  type="checkbox"
                  checked={selectedLeads.includes(lead._id)}
                  onChange={() => toggleLead(lead._id)}
                />
                <span>
                  {lead.name} - {lead.phone} - {lead.email} - {lead.company} - 
                  Status: {lead.status} - Type: {lead.type} - Source: {lead.source}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      {/* Templates Section */}
      <div className="saved-templates">
        <div className="templates-controls">
          <h3>💾 Saved Templates ({templates.length})</h3>
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            {searchTerm
              ? "🔍 No templates match your search"
              : "📭 No WhatsApp templates saved yet. Create your first one!"}
          </div>
        ) : (
          <div className="templates-grid">
            {filteredTemplates.map((t) => (
              <div key={t._id} className="template-item">
                <div className="template-content">
                  <h4>{t.templateName}</h4>
                  <p>{t.body}</p>
                  <div className="template-meta">
                    <span>Created: {t.createdAt ? new Date(t.createdAt).toLocaleString() : "N/A"}</span>
                  </div>
                </div>
                <div className="template-actions">
                  <button 
                    onClick={() => handleBulkSend(t._id)} 
                    className="send-btn"
                    disabled={!isLoggedIn}
                    title={!isLoggedIn ? "WhatsApp is not connected" : "Send to selected leads"}
                  >
                    Bulk Send 🚀
                  </button>
                  <button onClick={() => handleEdit(t)} className="edit-btn">Edit</button>
                  <button onClick={() => handleDelete(t._id)} className="delete-btn">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppTemplates;